import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import logger from '@/lib/logger';
import { Lightning, Check, Crown, Brain, Leaf, Wrench, ShareNetwork, Barbell, ArrowRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: 'monthly',
    name: 'PRO MONTHLY',
    price: '$4.99',
    period: '/month',
    savings: null,
  },
  {
    id: 'yearly',
    name: 'PRO YEARLY',
    price: '$29.99',
    period: '/year',
    savings: 'Save 50%',
  },
];

const PRO_FEATURES = [
  { icon: Brain, text: 'AI Workout Plan Generator', desc: 'Unlimited personalized AI plans' },
  { icon: Leaf, text: 'AI Nutrition Recommendations', desc: 'Smart meal frameworks for your goals' },
  { icon: Wrench, text: 'Custom Workout Builder', desc: 'Create and save your own routines' },
  { icon: ShareNetwork, text: 'Share Your Streak', desc: 'Social cards in 3 styles' },
];

const FREE_FEATURES = [
  '20+ bodyweight exercises with video demos',
  'Workout player with timer & form tips',
  'Streak tracking & habit system',
  'Mood & energy logging',
  'Stress reset sessions',
  'Community challenges',
  'Accountability buddy system',
];

export default function PricingPage() {
  const { user, updateUser } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  const fetchSubscription = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/user/subscription`);
      setSubscription(data);
    } catch (err) {
      logger.error('Fetch subscription error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSubscription(); }, [fetchSubscription]);

  const handlePurchase = async (planId) => {
    setPurchasing(planId);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/payments/create-checkout`, {
        plan_id: planId,
        origin_url: origin,
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      logger.error('Purchase error:', err);
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isPro = subscription?.is_pro;

  return (
    <div data-testid="pricing-page">
      <div className="mb-8 text-center">
        <div className="label-tactical text-[#FF3B30] mb-1">Upgrade</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter">
          {isPro ? 'YOUR PRO PLAN' : 'UNLOCK YOUR FULL POTENTIAL'}
        </h1>
        <p className="text-[#A1A1AA] mt-3 max-w-lg mx-auto">
          {isPro
            ? `You're on the ${subscription.plan === 'yearly' ? 'Yearly' : 'Monthly'} Pro plan. Enjoy all premium features.`
            : 'Go Pro for AI-powered plans, smart nutrition, custom workouts, and more.'}
        </p>
      </div>

      {isPro ? (
        <div className="max-w-md mx-auto">
          <div className="tactical-card p-8 border-[#FF3B30]/30 text-center">
            <Crown weight="fill" className="w-14 h-14 text-[#FF3B30] mx-auto mb-4" />
            <h2 className="text-3xl font-black tracking-tighter mb-2">PRO ACTIVE</h2>
            <p className="text-[#A1A1AA] mb-4">
              Plan: <span className="text-white font-bold uppercase">{subscription.plan}</span>
            </p>
            {subscription.expires && (
              <p className="text-sm text-[#71717A]">
                Renews: {new Date(subscription.expires).toLocaleDateString('en', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
            <div className="mt-6 space-y-2">
              {PRO_FEATURES.map(f => (
                <div key={f.text} className="flex items-center gap-2 text-sm text-[#34C759]">
                  <Check weight="bold" className="w-4 h-4" /> {f.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto mb-12">
            {PLANS.map(plan => (
              <div key={plan.id} className={`tactical-card p-6 text-center relative ${plan.id === 'yearly' ? 'border-[#FF3B30]/40' : ''}`} data-testid={`plan-${plan.id}`}>
                {plan.savings && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF3B30] text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
                    {plan.savings}
                  </div>
                )}
                <Crown weight={plan.id === 'yearly' ? 'fill' : 'bold'} className="w-10 h-10 text-[#FF3B30] mx-auto mb-3" />
                <h3 className="text-lg font-bold tracking-tight mb-1">{plan.name}</h3>
                <div className="text-4xl font-black font-['Barlow_Condensed'] mb-1">{plan.price}</div>
                <div className="text-sm text-[#71717A] mb-4">{plan.period}</div>
                <button
                  onClick={() => handlePurchase(plan.id)}
                  disabled={purchasing === plan.id}
                  className="btn-primary w-full py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid={`buy-${plan.id}`}
                >
                  {purchasing === plan.id ? 'Redirecting...' : (
                    <><Lightning weight="bold" className="w-5 h-5" /> Go Pro</>
                  )}
                </button>
              </div>
            ))}
          </div>

          {/* Feature Comparison */}
          <div className="max-w-3xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Free Tier */}
              <div className="tactical-card p-6">
                <h3 className="text-lg font-bold tracking-tight mb-4">FREE</h3>
                <div className="space-y-2">
                  {FREE_FEATURES.map(f => (
                    <div key={f} className="flex items-start gap-2 text-sm text-[#A1A1AA]">
                      <Check weight="bold" className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" /> {f}
                    </div>
                  ))}
                </div>
              </div>
              {/* Pro Tier */}
              <div className="tactical-card p-6 border-[#FF3B30]/20">
                <div className="flex items-center gap-2 mb-4">
                  <Crown weight="fill" className="w-5 h-5 text-[#FF3B30]" />
                  <h3 className="text-lg font-bold tracking-tight">PRO</h3>
                </div>
                <p className="text-xs text-[#71717A] mb-3">Everything in Free, plus:</p>
                <div className="space-y-3">
                  {PRO_FEATURES.map(f => (
                    <div key={f.text} className="flex items-start gap-2">
                      <f.icon weight="bold" className="w-5 h-5 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium">{f.text}</div>
                        <div className="text-xs text-[#71717A]">{f.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
